import React, { useMemo, useRef, useState } from 'react';
import { Box, Popover, Tooltip, Typography, IconButton } from '@mui/material';
import { Visibility } from '../../Icons/VisibilityIcon';
import { useWindowEvent } from '../../../hooks/useWindowEvent';
import {
  getCheckboxFiltersToDisplay,
  getFiltersToDisplay,
  getLabel,
  getRangeFiltersToDisplay,
} from './SelectedFilters.helpers';

import { styles } from './SelectedFilters.styles';

const SelectedFilterBox = ({ filterName, value, pl, isInline }) => (
  <Box pl={pl} display={isInline ? 'inline-block' : 'block'}>
    <Typography variant="body1" sx={styles.filterName}>
      {`${getLabel(filterName)}: `}
    </Typography>
    <Typography variant="body2" display="inline">
      {Array.isArray(value)
        ? value.map((v) => v.displayName || v.name || v).join(', ')
        : value.displayName || value.name || value}
    </Typography>
  </Box>
);

const SelectedFiltersPortfolio = ({ name, filters, index, checkListItems }) => {
  const filtersToDisplay = getFiltersToDisplay(filters);
  const checkboxesFiltersToDisplay = getCheckboxFiltersToDisplay(filters, checkListItems);
  const selectAndCheckboxFilters = { ...filtersToDisplay, ...checkboxesFiltersToDisplay };
  const rangeFiltersToDisplay = getRangeFiltersToDisplay(filters);

  return (
    <Box
      sx={styles.portfolioBox}
      borderTop={index !== 0 ? 'solid rgba(0, 0, 0, 0.12) thin' : undefined}
    >
      <Typography variant="h4_new">{`${name} ${index + 1}`}</Typography>
      {Object.keys(selectAndCheckboxFilters).map((key) => (
        <SelectedFilterBox
          pl={0.5}
          value={selectAndCheckboxFilters[key]}
          filterName={key}
          key={key}
        />
      ))}
      <div>
        {Object.keys(rangeFiltersToDisplay).map((key, index) => (
          <SelectedFilterBox
            pl={index !== 0 ? 3 : 0.5}
            value={rangeFiltersToDisplay[key]}
            filterName={key}
            key={key}
            isInline
          />
        ))}
      </div>
    </Box>
  );
};

// 'open' is never shown since there is no way to interact with the button when the popover is open
const tooltipTextMap = {
  open: 'Hide selected filters',
  closed: 'Shows selected filters',
  disabled: 'Shows selected portfolios',
};

export const SelectedFilters = ({ allFilters, checkListItems }) => {
  const [open, setOpenState] = useState(false);
  const anchorEl = useRef(null);

  useWindowEvent('resize', () => setOpenState(false), { passive: true });

  const noFilterChosen = useMemo(
    () =>
      allFilters.every(({ filters }) =>
        Object.entries(filters)
          .filter(([key]) => key !== 'name')
          .every(([key, value]) => {
            if (Array.isArray(value)) {
              return value.length === 0;
            }

            if (key === 'companyOperand') {
              return value === 'OR';
            }

            return !value;
          }),
      ),
    [allFilters],
  );

  const handleClick = () => setOpenState((state) => !state);

  const id = 'selected-filters-popover';

  const tooltipText = noFilterChosen ? tooltipTextMap.disabled : tooltipTextMap.closed;

  return (
    <Box sx={styles.wrapper}>
      <Tooltip title={tooltipText}>
        <Box sx={styles.buttonWrapper}>
          <IconButton
            aria-describedby={id}
            sx={styles.button}
            onClick={handleClick}
            size="medium"
            ref={anchorEl}
            disabled={noFilterChosen}
          >
            <Visibility visible={!open} />
          </IconButton>
        </Box>
      </Tooltip>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl.current}
        onClose={handleClick}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: styles.popoverPaper,
          },
        }}
      >
        <Box p={1}>
          <Typography variant="h3_new">Selected filters</Typography>
          {allFilters.map(({ name, filters }, index) => (
            <SelectedFiltersPortfolio
              name={name}
              filters={filters}
              index={index}
              key={name}
              checkListItems={checkListItems}
            />
          ))}
        </Box>
      </Popover>
    </Box>
  );
};
